import { useState } from 'react';

interface Integration {
  name: string;
  description: string;
  icon: React.ReactNode;
  actions: { label: string; href?: string; copy?: string }[];
  docsUrl: string;
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={async () => {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }}
      className="text-xs text-ll-muted hover:text-ll-cyan transition-colors font-mono"
      title="Copy to clipboard"
    >
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
}

function CodeSnippet({ code }: { code: string }) {
  return (
    <div className="bg-ll-bg border border-ll-border rounded-lg p-3 mt-3">
      <div className="flex items-start justify-between gap-2">
        <code className="text-xs text-ll-muted font-mono whitespace-pre-wrap break-all">
          {code}
        </code>
        <CopyButton text={code} />
      </div>
    </div>
  );
}

const INTEGRATIONS: Integration[] = [
  {
    name: 'VS Code',
    description:
      'Real-time linting, hover tooltips, and AI-powered code actions directly in your editor.',
    icon: (
      <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor">
        <path d="M17.583 2.267L12.2 7.15 8.067 4.017l-.717.4v15.183l.717.383L12.2 16.85l5.383 4.883L22 19.817V4.183zM2 6.217l4.35-2.1 5.917 5.833L6.35 15.783 2 17.883zm10.267 5.783l5.916-5.833v11.666z" />
      </svg>
    ),
    actions: [
      {
        label: 'Install from Marketplace',
        href: 'https://marketplace.visualstudio.com/items?itemName=layerlint.layerlint',
      },
    ],
    docsUrl: 'https://docs.layerlint.io/integrations/vscode',
  },
  {
    name: 'JetBrains IDEs',
    description:
      'IntelliJ IDEA, WebStorm, GoLand, and more. Gutter diagnostics and quick-fix actions.',
    icon: (
      <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor">
        <path d="M2 2h20v20H2zm2 16h8v2H4zm0-2h5v-1.5H4zM17.5 6.5L16 5l-4.5 4.5L16 14l1.5-1.5-3-3z" />
      </svg>
    ),
    actions: [
      {
        label: 'Install from JetBrains Marketplace',
        href: 'https://plugins.jetbrains.com/plugin/30685-layerlint',
      },
    ],
    docsUrl: 'https://docs.layerlint.io/integrations/jetbrains',
  },
  {
    name: 'CLI',
    description:
      'Lint Dockerfiles from the command line. Perfect for CI/CD pipelines and local development.',
    icon: (
      <svg
        viewBox="0 0 24 24"
        className="w-8 h-8"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="4 17 10 11 4 5" />
        <line x1="12" y1="19" x2="20" y2="19" />
      </svg>
    ),
    actions: [
      { label: 'Homebrew', copy: 'brew install layerlint/tap/layerlint' },
      { label: 'Scoop', copy: 'scoop install layerlint' },
    ],
    docsUrl: 'https://docs.layerlint.io/cli',
  },
  {
    name: 'Docker Desktop',
    description: 'Analyse Dockerfiles directly from Docker Desktop with a drag-and-drop interface.',
    icon: (
      <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor">
        <path d="M13.983 11.078h2.119a.186.186 0 00.186-.185V9.006a.186.186 0 00-.186-.186h-2.119a.185.185 0 00-.185.185v1.888c0 .102.083.185.185.185m-2.954 0h2.118a.186.186 0 00.186-.185V9.006a.186.186 0 00-.186-.186h-2.118a.185.185 0 00-.185.185v1.888c0 .102.082.185.185.185m-2.93 0h2.12a.186.186 0 00.184-.185V9.006a.186.186 0 00-.184-.186h-2.12a.185.185 0 00-.184.185v1.888c0 .102.083.185.185.185m-2.964 0h2.119a.186.186 0 00.185-.185V9.006a.186.186 0 00-.185-.186H5.136a.186.186 0 00-.186.186v1.887c0 .102.084.185.186.185m5.893-2.264h2.118a.186.186 0 00.186-.185V6.929a.185.185 0 00-.186-.185h-2.118a.185.185 0 00-.185.185v1.885c0 .102.082.185.185.185m-2.93 0h2.12a.186.186 0 00.184-.185V6.929a.185.185 0 00-.184-.185h-2.12a.186.186 0 00-.184.185v1.885c0 .102.083.185.185.185m-2.964 0h2.119a.186.186 0 00.185-.185V6.929a.185.185 0 00-.185-.185H5.136a.186.186 0 00-.186.186v1.884c0 .102.084.185.186.185m5.893-2.264h2.118a.186.186 0 00.186-.185V4.749a.185.185 0 00-.186-.185h-2.118a.186.186 0 00-.185.185v1.886c0 .102.082.185.185.185m-2.93 0h2.12a.185.185 0 00.184-.185V4.749a.185.185 0 00-.184-.185h-2.12a.186.186 0 00-.184.185v1.886c0 .102.083.185.185.185m10.607 2.264h2.118a.187.187 0 00.186-.186V9.006a.186.186 0 00-.186-.186h-2.118a.186.186 0 00-.186.186v1.887c0 .102.084.185.186.185" />
      </svg>
    ),
    actions: [
      {
        label: 'Open in Docker Desktop',
        href: 'https://hub.docker.com/extensions/layerlint/layerlint-extension',
      },
    ],
    docsUrl: 'https://docs.layerlint.io/integrations/docker-desktop',
  },
  {
    name: 'GitHub Action',
    description:
      'Automated Dockerfile linting on every push and pull request with inline annotations.',
    icon: (
      <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor">
        <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
      </svg>
    ),
    actions: [],
    docsUrl: 'https://docs.layerlint.io/integrations/github-action',
  },
  {
    name: 'GitLab CI',
    description:
      'CI component with Code Quality reports, merge request discussions, and commit status.',
    icon: (
      <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor">
        <path d="M4.845.904c-.435 0-.82.28-.955.692C2.639 5.449 1.246 9.728.07 13.335a1.437 1.437 0 00.522 1.607l11.071 8.045c.2.145.472.144.67-.004l11.073-8.04a1.436 1.436 0 00.522-1.61c-1.285-3.942-2.683-8.256-3.817-11.746a1.004 1.004 0 00-.957-.684.987.987 0 00-.949.69l-2.405 7.408H8.203l-2.41-7.408a.987.987 0 00-.942-.69h-.006z" />
      </svg>
    ),
    actions: [],
    docsUrl: 'https://docs.layerlint.io/integrations/gitlab-ci',
  },
];

const GITHUB_ACTION_SNIPPET = `- uses: layerlint/action@v1
  with:
    api-key: \${{ secrets.LAYERLINT_API_KEY }}`;

const GITLAB_CI_SNIPPET = `include:
  - component: layerlint/layerlint-ci-component/layerlint@1
    inputs:
      api-key: $LAYERLINT_API_KEY`;

export function Downloads() {
  return (
    <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
      <div className="text-center mb-12">
        <h1 className="text-4xl sm:text-5xl font-bold text-ll-text mb-4">
          Integrations & Downloads
        </h1>
        <p className="text-lg text-ll-muted max-w-xl mx-auto">
          Use LayerLint wherever you write Dockerfiles.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {INTEGRATIONS.map((integration) => (
          <div
            key={integration.name}
            className="bg-ll-surface border border-ll-border rounded-2xl p-6 flex flex-col transition-shadow duration-300 hover:shadow-[0_8px_40px_rgba(0,229,200,0.08)]"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="text-ll-cyan">{integration.icon}</div>
              <h2 className="text-lg font-bold text-ll-text">{integration.name}</h2>
            </div>
            <p className="text-sm text-ll-muted mb-4 flex-1">{integration.description}</p>

            {/* Special snippets for CI integrations */}
            {integration.name === 'GitHub Action' && <CodeSnippet code={GITHUB_ACTION_SNIPPET} />}
            {integration.name === 'GitLab CI' && <CodeSnippet code={GITLAB_CI_SNIPPET} />}

            {integration.actions.map((action) =>
              action.copy ? (
                <div
                  key={action.label}
                  className="bg-ll-bg border border-ll-border rounded-lg p-3 mt-3"
                >
                  <div className="flex items-center justify-between gap-2">
                    <code className="text-xs text-ll-muted font-mono truncate">{action.copy}</code>
                    <CopyButton text={action.copy} />
                  </div>
                </div>
              ) : action.href ? (
                <a
                  key={action.label}
                  href={action.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 w-full rounded-lg px-4 py-2.5 bg-transparent border border-ll-border text-sm text-ll-text hover:border-ll-cyan hover:text-ll-cyan transition-all text-center font-medium"
                >
                  {action.label}
                </a>
              ) : null,
            )}

            <a
              href={integration.docsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 text-xs text-ll-muted hover:text-ll-cyan transition-colors text-center"
            >
              View documentation &rarr;
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}
